import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Filter, ChevronDown } from 'lucide-react';

const SubcategoryAnalysis = ({ data }) => {
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  
  // Process data based on sentiment filter
  const getFilteredData = () => {
    if (sentimentFilter === 'all') {
      return data;
    }
    
    // For other sentiment filters, we'll use the sentiment data if available
    return data.map(item => ({
      ...item,
      value: item[sentimentFilter] || 0, // Use the specific sentiment value
    })).filter(item => item.value > 0) // Only include items with positive values
      .sort((a, b) => b.value - a.value);
  };
  
  const filteredData = getFilteredData();
  
  // Custom colors for bars
  const getBarColor = (subcategory) => {
    // Extract the main category from the subcategory string (format: "category:subcategory")
    const category = subcategory.split(':')[0];
    
    // Define colors for each main category
    const categoryColors = {
      customer_service: '#4f46e5', // indigo
      digital_banking: '#8b5cf6', // violet
      account: '#ec4899', // pink
      branch: '#f97316', // orange
      cards: '#0ea5e9', // light blue
      fees: '#22c55e', // green
      loans: '#f43f5e', // rose
      investment: '#06b6d4', // cyan
      payment: '#eab308', // yellow
      security: '#6366f1', // indigo (lighter)
    };
    
    return categoryColors[category] || '#94a3b8'; // Default to gray if category not found
  };
  
  // Format data for the chart
  const formattedData = filteredData.map(item => {
    // Split the subcategory name into main category and subcategory parts
    const [category, subcategory] = item.name.split(':');
    
    return {
      ...item,
      formattedName: subcategory.replace('_', ' '),
      category: category.replace('_', ' '),
      color: getBarColor(item.name)
    };
  });
  
  // Sentiment colors - consistent with other components
  const SENTIMENT_COLORS = {
    positive: '#10b981', // Green
    neutral: '#9ca3af', // Gray
    negative: '#ef4444', // Red
  };
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="text-sm font-medium">{data.formattedName}</p>
          <p className="text-xs text-gray-600">Category: {data.category}</p>
          <p className="text-sm font-semibold mt-1">{data.value} mentions</p>
          {sentimentFilter === 'all' && data.positive !== undefined && (
            <div className="text-xs mt-2 pt-1 border-t border-gray-100">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: SENTIMENT_COLORS.positive }}></div>
                <span>Positive: {data.positive || 0}</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: SENTIMENT_COLORS.neutral }}></div>
                <span>Neutral: {data.neutral || 0}</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: SENTIMENT_COLORS.negative }}></div>
                <span>Negative: {data.negative || 0}</span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Get sentiment filter label
  const getSentimentFilterLabel = () => {
    switch (sentimentFilter) {
      case 'positive': return 'Positive Mentions';
      case 'negative': return 'Negative Mentions';
      case 'neutral': return 'Neutral Mentions';
      default: return 'All Mentions';
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Top Subcategories</h3>
          <p className="text-sm text-gray-500">Top 10 most mentioned subcategories</p>
        </div>
        <div className="flex space-x-2 items-center">
          <div className="relative">
            <button 
              className="flex items-center space-x-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
              onClick={() => setShowFilterMenu(!showFilterMenu)}
            >
              <Filter className="w-3.5 h-3.5 text-gray-500" />
              <span>{getSentimentFilterLabel()}</span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>
            
            {showFilterMenu && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${sentimentFilter === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => {
                      setSentimentFilter('all');
                      setShowFilterMenu(false);
                    }}
                  >
                    All Mentions
                  </button>
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${sentimentFilter === 'positive' ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => {
                      setSentimentFilter('positive');
                      setShowFilterMenu(false);
                    }}
                  >
                    Positive Mentions
                  </button>
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${sentimentFilter === 'neutral' ? 'bg-gray-50 text-gray-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => {
                      setSentimentFilter('neutral');
                      setShowFilterMenu(false);
                    }}
                  >
                    Neutral Mentions
                  </button>
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${sentimentFilter === 'negative' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => {
                      setSentimentFilter('negative');
                      setShowFilterMenu(false);
                    }}
                  >
                    Negative Mentions
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={true} stroke="#d1d5db" strokeWidth={1} />
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis
              dataKey="formattedName"
              type="category"
              axisLine={false}
              tickLine={false}
              width={120}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="value" 
              radius={[0, 4, 4, 0]}
              barSize={20}
            >
              {formattedData.map((entry, index) => {
                // Always use category color regardless of sentiment filter
                return (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <div className="text-sm font-medium">Category Color Legend:</div>
          {sentimentFilter !== 'all' && (
            <div className="text-xs text-gray-500">
              Showing {sentimentFilter} mentions only (by category color)
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries({
            customer_service: '#4f46e5', // indigo
            digital_banking: '#8b5cf6', // violet
            account: '#ec4899', // pink
            branch: '#f97316', // orange
            cards: '#0ea5e9', // light blue
          }).map(([category, color]) => (
            <div key={category} className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-1.5"
                style={{ backgroundColor: color }}
              ></div>
              <div className="text-xs font-medium">{category.replace('_', ' ')}</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default SubcategoryAnalysis;